import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, LogIn } from "lucide-react";
import { MastroBrand } from "@/components/MastroBrand";
import { LandingSection } from "@/components/landing/LandingSection";
import { WhatsAppConversation } from "@/components/landing/WhatsAppConversation";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { JobMemory } from "@/components/landing/JobMemory";
import { DashboardPreview } from "@/components/landing/DashboardPreview";
import { QuoteOutcome } from "@/components/landing/QuoteOutcome";
import { FinalCta } from "@/components/landing/FinalCta";
import { WhatsAppDisclosureDialog } from "@/components/landing/WhatsAppDisclosureDialog";
import { LANDING } from "@/constants/testIds";

export default function Landing() {
  const [discOpen, setDiscOpen] = useState(false);
  const openDisclosure = () => setDiscOpen(true);
  return (
    <div
      data-testid={LANDING.page}
      className="min-h-screen w-full bg-white text-neutral-950 flex flex-col"
    >
      <LandingSection
        as="header"
        className="border-b border-neutral-100"
        innerClassName="flex items-center justify-between py-4"
      >
        <span data-testid={LANDING.brand}>
          <MastroBrand className="h-11" />
        </span>
        <Link
          to="/login"
          data-testid={LANDING.ctaLogin}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-neutral-600 transition-colors hover:text-neutral-950"
        >
          <LogIn className="h-4 w-4" />
          Accedi
        </Link>
      </LandingSection>

      <main className="flex-1">
        <LandingSection
          data-testid={LANDING.hero}
          className="bg-neutral-950"
          innerClassName="py-10 sm:pt-14 sm:pb-28"
        >
          <div className="flex flex-col gap-10 sm:gap-10 sm:grid sm:grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] lg:items-start lg:gap-14">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col justify-center min-h-[calc(100vh-150px)] min-h-[calc(100dvh-150px)] max-w-2xl sm:min-h-0 sm:justify-start lg:max-w-none lg:pt-4"
            >
              <span className="inline-flex w-fit max-w-max shrink-0 self-start items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-teal-300 ring-1 ring-inset ring-white/15">
                <MessageCircle className="h-3.5 w-3.5" />
                Il tuo assistente di lavoro, su WhatsApp.
              </span>
              <h1 className="mt-10 text-[2rem] font-extrabold leading-[1.2] tracking-tight text-white sm:mt-7 sm:text-4xl sm:leading-[1.12] lg:text-[2.5rem] xl:text-[2.75rem]">
                Modifiche ed extra mentre lavori?
                <br />
                <span className="text-teal-400">Mastro tiene il filo.</span>
              </h1>
              <p className="mt-9 max-w-xl text-base leading-relaxed text-neutral-300 sm:mt-7 sm:text-lg">
                Una modifica richiesta dal cliente, un extra concordato con il geometra o un dettaglio da segnare.
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300 sm:mt-3 sm:text-lg">
                Apri la chat con Mastro su WhatsApp, invia un vocale, una foto o un messaggio: tutto resta collegato al lavoro giusto e lo ritrovi quando serve.
              </p>

              <div className="mt-16 flex flex-col gap-3 sm:mt-14 sm:flex-row sm:items-center sm:gap-4">
                <button
                  type="button"
                  onClick={openDisclosure}
                  data-testid={LANDING.ctaWhatsapp}
                  className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-teal-500 px-9 py-5 text-lg font-bold text-neutral-950 shadow-[0_6px_16px_-10px_rgba(20,184,166,0.35)] transition-colors hover:bg-teal-400"
                >
                  <MessageCircle className="h-5 w-5" />
                  Prova Mastro su WhatsApp
                </button>
                <Link
                  to="/login"
                  data-testid={LANDING.ctaLoginHero}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-transparent px-5 py-3 text-sm font-medium text-neutral-400 transition-colors hover:border-white/25 hover:text-neutral-200"
                >
                  <LogIn className="h-4 w-4" />
                  Accedi
                </Link>
              </div>
            </motion.div>

            {/* MS-3: Hero visual — presentational WhatsApp conversation. */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              data-testid="landing-hero-visual"
              className="w-full lg:flex lg:justify-end"
            >
              <WhatsAppConversation />
            </motion.div>
          </div>
        </LandingSection>

        <LandingSection innerClassName="py-20 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            <HowItWorks />
          </motion.div>
        </LandingSection>

        <LandingSection
          className="border-y border-neutral-200/70 bg-[#F6F6F4]"
          innerClassName="py-20 sm:py-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4 }}
          >
            <JobMemory />
          </motion.div>
        </LandingSection>

        <LandingSection innerClassName="py-20 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4 }}
          >
            <DashboardPreview />
          </motion.div>
        </LandingSection>

        <LandingSection
          className="border-y border-neutral-200/70 bg-[#F6F6F4]"
          innerClassName="py-20 sm:py-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4 }}
          >
            <QuoteOutcome />
          </motion.div>
        </LandingSection>

        <LandingSection className="bg-neutral-950" innerClassName="py-16 sm:py-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
          >
            <FinalCta onOpen={openDisclosure} />
          </motion.div>
        </LandingSection>
      </main>

      <LandingSection
        as="footer"
        className="border-t border-neutral-100"
        innerClassName="flex flex-col gap-2 py-6 text-xs text-neutral-400 sm:flex-row sm:items-center sm:justify-between"
      >
        <span>
          Mastro — la memoria operativa del professionista, direttamente sul tuo WhatsApp.
        </span>
        <div className="flex items-center gap-4">
          <Link
            to="/privacy"
            data-testid="landing-footer-link-privacy"
            className="text-neutral-500 hover:text-neutral-700"
          >
            Privacy Policy
          </Link>
          <Link
            to="/termini"
            data-testid="landing-footer-link-terms"
            className="text-neutral-500 hover:text-neutral-700"
          >
            Termini
          </Link>
        </div>
      </LandingSection>

      <WhatsAppDisclosureDialog open={discOpen} onOpenChange={setDiscOpen} />
    </div>
  );
}
